import { useEffect, useState } from 'react';
import { PAYMENT_METHOD_LABELS } from '../vendas/helpers';
import { toDateInputValue } from './utils';
import { FinanceiroService } from './Service';

const service = new FinanceiroService();

export default function Configuracoes() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);
    const [defaultPaymentMethod, setDefaultPaymentMethod] = useState('');
    const [commissionBasePercent, setCommissionBasePercent] = useState('100');
    const [reprocessStart, setReprocessStart] = useState(() => {
        const start = new Date();
        start.setMonth(start.getMonth() - 1);
        return toDateInputValue(start);
    });
    const [reprocessEnd, setReprocessEnd] = useState(() => toDateInputValue(new Date()));
    const [reprocessing, setReprocessing] = useState(false);
    const [reprocessResult, setReprocessResult] = useState<any>(null);

    useEffect(() => {
        service
            .settings()
            .then(({ data }) => {
                setDefaultPaymentMethod(data?.defaultPaymentMethod ?? '');
                setCommissionBasePercent(String(data?.subscriptionCommissionBasePercent ?? 100));
            })
            .catch((e: any) => {
                setError(String(e?.response?.data?.message || 'Erro ao carregar as configurações'));
                console.error(e);
            })
            .finally(() => setLoading(false));
    }, []);

    const save = async () => {
        setSaving(true);
        setSaved(false);
        setError(null);
        try {
            await service.settingsUpdate({
                defaultPaymentMethod: defaultPaymentMethod || null,
                subscriptionCommissionBasePercent: Number(commissionBasePercent),
            });
            setSaved(true);
        } catch (e: any) {
            setError(String(e?.response?.data?.message || 'Erro ao salvar as configurações'));
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    const reprocess = async () => {
        setReprocessing(true);
        setReprocessResult(null);
        setError(null);
        try {
            const { data } = await service.reprocess({ startAt: reprocessStart, endAt: reprocessEnd });
            setReprocessResult(data);
        } catch (e: any) {
            setError(String(e?.response?.data?.message || 'Erro ao reprocessar os lançamentos'));
            console.error(e);
        } finally {
            setReprocessing(false);
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="card bg-base-100 shadow">
                <div className="card-body flex max-w-xl flex-col gap-4">
                    {error && (
                        <div className="alert alert-error">
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="flex flex-col">
                        <label className="label py-1" htmlFor="default-payment-method">
                            <span className="label-text">Forma de pagamento padrão</span>
                        </label>
                        <select
                            id="default-payment-method"
                            className="select select-bordered select-sm w-full md:w-64"
                            value={defaultPaymentMethod}
                            disabled={loading}
                            onChange={(e) => setDefaultPaymentMethod(e.target.value)}
                        >
                            <option value="">Sem padrão</option>
                            {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>
                                    {label}
                                </option>
                            ))}
                        </select>
                        <p className="mt-1 text-xs opacity-70">
                            Vem selecionada ao concluir e é usada nos atendimentos concluídos automaticamente. A equipe
                            pode alterar depois em cada atendimento. Sem padrão, os atendimentos automáticos ficam fora
                            do caixa até alguém registrar o pagamento.
                        </p>
                    </div>

                    <div className="flex flex-col">
                        <label className="label py-1" htmlFor="commission-base-percent">
                            <span className="label-text">Base da comissão em assinaturas (% do preço normal)</span>
                        </label>
                        <input
                            id="commission-base-percent"
                            type="number"
                            min={0}
                            max={100}
                            className="input input-bordered input-sm w-full md:w-44"
                            value={commissionBasePercent}
                            disabled={loading}
                            onChange={(e) => setCommissionBasePercent(e.target.value)}
                        />
                        <p className="mt-1 text-xs opacity-70">
                            Com 100%, o profissional recebe o percentual dele sobre o preço normal do serviço coberto
                            pela assinatura.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={save}
                            disabled={loading || saving || commissionBasePercent === ''}
                        >
                            {saving && <span className="loading loading-spinner loading-sm"></span>}
                            Salvar
                        </button>
                        {saved && <span className="text-sm opacity-70">Configurações salvas.</span>}
                    </div>
                </div>
            </div>

            <div className="card bg-base-100 shadow">
                <div className="card-body flex max-w-xl flex-col gap-3">
                    <h2 className="font-medium">Reprocessar lançamentos</h2>
                    <p className="text-xs opacity-70">
                        Cria o que faltar e acerta valores alterados depois da conclusão. Pode ser repetido sem duplicar
                        lançamentos. Períodos longos podem levar alguns minutos.
                    </p>
                    <div className="flex flex-col gap-3 md:flex-row md:items-end">
                        <div className="flex flex-col">
                            <label className="label py-1" htmlFor="reprocess-start">
                                <span className="label-text">De</span>
                            </label>
                            <input
                                id="reprocess-start"
                                type="date"
                                className="input input-bordered input-sm w-full md:w-44"
                                value={reprocessStart}
                                onChange={(e) => setReprocessStart(e.target.value)}
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="label py-1" htmlFor="reprocess-end">
                                <span className="label-text">Até</span>
                            </label>
                            <input
                                id="reprocess-end"
                                type="date"
                                className="input input-bordered input-sm w-full md:w-44"
                                value={reprocessEnd}
                                onChange={(e) => setReprocessEnd(e.target.value)}
                            />
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={reprocess}
                            disabled={reprocessing || !reprocessStart || !reprocessEnd}
                        >
                            {reprocessing && <span className="loading loading-spinner loading-sm"></span>}
                            Reprocessar
                        </button>
                    </div>
                    {reprocessResult && (
                        <p className="text-sm" role="status">
                            Concluído: {reprocessResult.horariosMatched} atendimentos, {reprocessResult.vendasMatched}{' '}
                            vendas, {reprocessResult.periodosMatched} períodos e {reprocessResult.comprasMatched}{' '}
                            compras analisados
                            {reprocessResult.errors ? `, com ${reprocessResult.errors} erros` : ''}.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
